import {
  Alert,
  BarChart,
  Box,
  ColumnLayout,
  Container,
  Header,
  Link,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { getZipBlastMetrics } from '../../common/api/zipBlasts';
import { MetricComparison } from '../stats/metrics';
import { ZipBlastStatusBadge } from '../ZipBlastStatusBadge';

export type ZipBlastMetricsPanelProps = {
  campaignUuid?: string | null;
};

/**
 * Aggregate zip-blast operational metrics: funnel counters, status breakdown
 * graph, active/stuck counts, and recent failures.
 */
export function ZipBlastMetricsPanel(props: ZipBlastMetricsPanelProps) {
  const navigate = useNavigate();
  const metricsQuery = useQuery({
    queryKey: ['zip-blast-metrics', props.campaignUuid ?? null],
    queryFn: () => getZipBlastMetrics(props.campaignUuid),
    refetchInterval: 30_000,
  });

  const data = metricsQuery.data;
  const loading = metricsQuery.isPending || metricsQuery.isFetching;

  const statusSeries = Object.entries(data?.status_counts ?? {})
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => ({ x: status, y: count }));

  if (metricsQuery.error) {
    return (
      <Alert type="error" header="Failed to load zip blast metrics">
        {String(metricsQuery.error)}
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l" direction="vertical">
      <Container
        header={
          <Header
            variant="h2"
            counter={data ? `(${data.total_requests.toLocaleString()} blasts)` : undefined}
            description="Aggregate funnel counters across zip blasts"
          >
            Zip Blast Metrics
          </Header>
        }
      >
        <ColumnLayout columns={4} variant="text-grid">
          <MetricComparison
            header="Listings found"
            value={data?.listings_found}
            loading={loading}
          />
          <MetricComparison
            header="Places resolved"
            value={data?.places_resolved}
            loading={loading}
          />
          <MetricComparison header="Reports built" value={data?.reports_built} loading={loading} />
          <MetricComparison
            header="Contacts created"
            value={data?.contacts_created}
            loading={loading}
          />
          <MetricComparison header="Leads pushed" value={data?.leads_pushed} loading={loading} />
          <MetricComparison
            header="Emails scheduled"
            value={data?.emails_scheduled}
            loading={loading}
          />
          <MetricComparison header="Active blasts" value={data?.active_count} loading={loading} />
          <MetricComparison
            header="Stuck blasts"
            value={data?.stuck_count}
            loading={loading}
            inverted
          />
        </ColumnLayout>
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            description="Operational rollups for City / area blasts and child ZIP batches."
          >
            Area rollups
          </Header>
        }
      >
        <ColumnLayout columns={3} variant="text-grid">
          <MetricComparison
            header="Total requests"
            value={data?.total_requests}
            loading={loading}
          />
          <MetricComparison header="Active blasts" value={data?.active_count} loading={loading} />
          <MetricComparison
            header="Stuck blasts"
            value={data?.stuck_count}
            loading={loading}
            inverted
          />
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2">Blasts by status</Header>}>
        <BarChart
          statusType={loading && !data ? 'loading' : 'finished'}
          height={220}
          hideFilter
          series={[{ type: 'bar', title: 'Blasts', data: statusSeries }]}
          xTitle="Status"
          yTitle="Blasts"
          empty={
            <Box textAlign="center" color="inherit">
              <b>No zip blasts yet.</b>
            </Box>
          }
        />
      </Container>

      {data && data.recent_failures.length > 0 ? (
        <Container header={<Header variant="h2">Recent failures</Header>}>
          <Table
            variant="embedded"
            items={data.recent_failures}
            columnDefinitions={[
              {
                id: 'zip',
                header: 'Zip',
                cell: (i) => (
                  <Link
                    href={`/dashboard/zip-blasts/${i.uuid}`}
                    onFollow={(e) => {
                      e.preventDefault();
                      navigate({ to: '/dashboard/zip-blasts/$uuid', params: { uuid: i.uuid } });
                    }}
                  >
                    {i.zip_code}
                  </Link>
                ),
                width: 110,
              },
              {
                id: 'status',
                header: 'Status',
                cell: (i) => <ZipBlastStatusBadge status={i.status} />,
                width: 160,
              },
              {
                id: 'error',
                header: 'Error',
                cell: (i) => i.error_message ?? '—',
              },
              {
                id: 'updated',
                header: 'Updated',
                cell: (i) => new Date(i.updated_at).toLocaleString(),
                width: 200,
              },
            ]}
          />
        </Container>
      ) : null}
    </SpaceBetween>
  );
}
